import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CriteriaBuilder } from '@/components/StoreModels/CriteriaBuilder'
import { ProcessingStatusCard } from '@/components/StoreModels/ProcessingStatusCard'
import {
  DEFAULT_CRITERIA,
  buildProcessedCriteria,
} from '@/components/StoreModels/criteriaUtils'
import { useUpdateStoreModel } from '@/hooks/useStoreModels'
import { useProcessingStatus } from '@/hooks/useProcessingStatus'
import { toast } from '@/stores/useToastStore'
import type { AxiosError } from 'axios'
import type {
  StoreModelDetailRead,
  AllocationBasis,
  StrongModelCriteria,
  ProcessingResponse,
  StoreModelUpdate,
} from '@/types'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  allocation_basis: z.enum(['UNITS', 'SALES'] as const),
  description: z.string().max(1000).optional().or(z.literal('')),
  recalculation_schedule: z.string().optional().or(z.literal('')),
})

type FormValues = z.infer<typeof formSchema>

interface StoreModelUpdateTabProps {
  model: StoreModelDetailRead
  onUpdated: () => void
}

export function StoreModelUpdateTab({
  model,
  onUpdated,
}: StoreModelUpdateTabProps) {
  const updateMutation = useUpdateStoreModel()

  const [criteria, setCriteria] = useState<StrongModelCriteria>(
    () => model.criteria ?? DEFAULT_CRITERIA
  )
  const [useAllStores, setUseAllStores] = useState(
    () => model.use_all_stores ?? true
  )
  const [processingResponse, setProcessingResponse] =
    useState<ProcessingResponse | null>(null)

  const processingStatus = useProcessingStatus(processingResponse)
  const statusCardRef = useRef<HTMLDivElement>(null)

  // Scroll to the processing status card when processing starts
  useEffect(() => {
    if (processingStatus.state !== 'idle') {
      statusCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [processingStatus.state])

  const isFormDisabled =
    updateMutation.isPending || processingStatus.state === 'processing'

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: model.name,
      allocation_basis: model.allocation_basis,
      description: model.description ?? '',
      recalculation_schedule: model.recalculation_schedule ?? '',
    },
  })

  // Reset form when model changes (e.g. after refetch)
  useEffect(() => {
    reset({
      name: model.name,
      allocation_basis: model.allocation_basis,
      description: model.description ?? '',
      recalculation_schedule: model.recalculation_schedule ?? '',
    })
    setCriteria(model.criteria ?? DEFAULT_CRITERIA)
    setUseAllStores(model.use_all_stores ?? true)
  }, [model, reset])

  // When processing completes, clear response and trigger onUpdated
  useEffect(() => {
    if (processingStatus.state === 'complete') {
      setProcessingResponse(null)
      processingStatus.reset()
      onUpdated()
    } else if (processingStatus.state === 'error') {
      setProcessingResponse(null)
      processingStatus.reset()
    }
  }, [processingStatus.state]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentAllocationBasis = watch('allocation_basis')

  const onSubmit = (data: FormValues) => {
    const processedCriteria = buildProcessedCriteria(criteria)

    const payload: StoreModelUpdate = {
      name: data.name,
      description: data.description || null,
      criteria: processedCriteria,
      allocation_basis: data.allocation_basis,
      use_all_stores: useAllStores,
      recalculation_schedule: data.recalculation_schedule || null,
    }

    updateMutation.mutate(
      { modelId: model.id, payload },
      {
        onSuccess: response => {
          setProcessingResponse(response)
        },
        onError: error => {
          const axiosError = error as AxiosError<{ detail?: string }>
          const message =
            axiosError.response?.data?.detail ?? 'Failed to update store model'
          toast.error(message)
        },
      }
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div ref={statusCardRef}>
        <ProcessingStatusCard status={processingStatus} />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="update-name">Name *</Label>
                <Input
                  id="update-name"
                  placeholder="Enter model name"
                  disabled={isFormDisabled}
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>Model Type</Label>
                <Input value={model.model_type} disabled className="bg-muted" />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="update-allocation_basis">
                  Allocation Basis
                </Label>
                <Select
                  value={currentAllocationBasis}
                  onValueChange={(v: AllocationBasis) =>
                    setValue('allocation_basis', v)
                  }
                  disabled={isFormDisabled}
                >
                  <SelectTrigger id="update-allocation_basis">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UNITS">Units</SelectItem>
                    <SelectItem value="SALES">Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="update-recalculation_schedule">
                  Recalculation Schedule
                </Label>
                <Input
                  id="update-recalculation_schedule"
                  placeholder="e.g. 0 0 * * MON"
                  disabled={isFormDisabled}
                  {...register('recalculation_schedule')}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="update-description">Description</Label>
              <Textarea
                id="update-description"
                placeholder="Optional description for this model"
                disabled={isFormDisabled}
                {...register('description')}
              />
              {errors.description && (
                <p className="text-sm text-destructive">
                  {errors.description.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Criteria */}
        <Card>
          <CardHeader>
            <CardTitle>Criteria</CardTitle>
          </CardHeader>
          <CardContent>
            <CriteriaBuilder
              criteria={criteria}
              onChange={setCriteria}
              disabled={isFormDisabled}
              useAllStores={useAllStores}
              onUseAllStoresChange={setUseAllStores}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            type="submit"
            className="cursor-pointer"
            disabled={isFormDisabled}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update Model'}
          </Button>
        </div>
      </form>
    </div>
  )
}
